import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import financeService from '../services/financeService';
import admissionService from '../services/admissionService';
import studentService from '../services/studentService';

/* ─────────────────────────────────────────────
   HELPERS
─────────────────────────────────────────────── */
const formatDate = (str) => {
  if (!str) return '';
  try {
    const d = new Date(str);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return str; }
};

// Same logic as StudentAnalytics — parse installments into schedule items
// Status is date-based: if due date is in the past → 'paid', else → 'pending'
const parseInstallments = (admission, totalFees) => {
  if (!admission) return [];

  // 1. Use normalized installments array from admissionService
  let raw = admission.installments;

  // 2. Fallback: build from flat inst1..inst4 fields
  if (!raw || !Array.isArray(raw) || raw.length === 0) {
    raw = [admission.inst1, admission.inst2, admission.inst3, admission.inst4]
      .filter(d => d != null && d !== '');
  }
  if (!raw || raw.length === 0) return [];

  const total     = totalFees || 0;
  const count     = raw.length;
  const perSlot   = count > 0 ? Math.floor(total / count) : 0;
  const remainder = total - perSlot * (count - 1);
  const today     = new Date();
  today.setHours(0, 0, 0, 0); // compare date only, not time

  return raw.map((item, idx) => {
    const isObj   = item && typeof item === 'object';
    const dateStr = isObj ? (item.date || item.dueDate || '') : item;

    // Parse amount — use provided value, or distribute total evenly
    const amount = isObj && item.amount
      ? Number(item.amount)
      : (idx === count - 1 ? remainder : perSlot);

    // Parse status — use provided status, or derive from whether due date is past
    let status = 'pending';
    if (isObj && item.status) {
      status = item.status;
    } else if (dateStr) {
      // Parse ISO date string safely (avoid UTC-vs-local timezone issues)
      const parts = String(dateStr).split('T')[0].split('-');
      if (parts.length === 3) {
        const due = new Date(
          parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2])
        );
        status = due < today ? 'paid' : 'pending';
      }
    }

    return {
      date:   dateStr ? formatDate(dateStr) : `Instalment ${idx + 1}`,
      amount,
      status,
    };
  });
};

/* ─────────────────────────────────────────────
   COLLECT PAYMENT MODAL
─────────────────────────────────────────────── */
const CollectPaymentModal = ({ admissionId, studentName, balance, onClose, onSuccess }) => {
  const [amount,  setAmount]  = useState('');
  const [mode,    setMode]    = useState('CASH');
  const [txnId,   setTxnId]   = useState('');
  const [date,    setDate]    = useState(new Date().toISOString().split('T')[0]);
  const [remarks, setRemarks] = useState('');
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');

  const inp = {
    width: '100%', padding: '12px 15px', border: '2px solid #E8E4DF',
    borderRadius: 6, fontSize: 14, fontFamily: 'Work Sans, sans-serif',
    outline: 'none', boxSizing: 'border-box',
  };

  const handleCollect = async () => {
    if (!amount || parseFloat(amount) <= 0) { setError('Please enter a valid payment amount'); return; }
    if (parseFloat(amount) > balance) { setError(`Amount cannot exceed balance ₹${Number(balance).toLocaleString('en-IN')}`); return; }
    setSaving(true); setError('');
    try {
      await financeService.collectPayment({
        admissionId, amount: parseFloat(amount),
        paymentMode: mode, transactionId: txnId || null,
        paymentDate: date, remarks: remarks || null,
      });
      onSuccess();
    } catch (e) {
      setError(e.response?.data?.message || 'Payment failed. Please try again.');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: 'white', padding: 40, borderRadius: 12, maxWidth: 500, width: '90%' }} onClick={e => e.stopPropagation()}>

        <div style={{ marginBottom: 30 }}>
          <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 28, color: '#1B3B5F', marginBottom: 10 }}>Collect Payment</div>
          <div style={{ color: '#6B6B6B', fontSize: 14 }}>{studentName} — Balance: ₹{Number(balance || 0).toLocaleString('en-IN')}</div>
        </div>

        {error && <div style={{ background: '#F8D7DA', border: '1px solid #F5C6CB', borderRadius: 6, padding: '10px 14px', marginBottom: 16, color: '#721C24', fontSize: 13 }}>{error}</div>}

        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, color: '#6B6B6B', marginBottom: 8 }}>Payment Amount (₹)</label>
          <input type="number" style={inp} placeholder="Enter amount" value={amount} onChange={e => setAmount(e.target.value)}
            onFocus={e => e.target.style.borderColor = '#1B3B5F'} onBlur={e => e.target.style.borderColor = '#E8E4DF'} />
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, color: '#6B6B6B', marginBottom: 8 }}>Payment Mode</label>
          <select style={{ ...inp, cursor: 'pointer' }} value={mode} onChange={e => setMode(e.target.value)}>
            <option value="CASH">Cash</option>
            <option value="CARD">Card</option>
            <option value="UPI">UPI</option>
            <option value="CHEQUE">Cheque</option>
            <option value="BANK_TRANSFER">Bank Transfer</option>
          </select>
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, color: '#6B6B6B', marginBottom: 8 }}>Transaction ID / Reference</label>
          <input type="text" style={inp} placeholder="Optional" value={txnId} onChange={e => setTxnId(e.target.value)}
            onFocus={e => e.target.style.borderColor = '#1B3B5F'} onBlur={e => e.target.style.borderColor = '#E8E4DF'} />
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, color: '#6B6B6B', marginBottom: 8 }}>Payment Date</label>
          <input type="date" style={inp} value={date} onChange={e => setDate(e.target.value)}
            onFocus={e => e.target.style.borderColor = '#1B3B5F'} onBlur={e => e.target.style.borderColor = '#E8E4DF'} />
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, color: '#6B6B6B', marginBottom: 8 }}>Remarks (Optional)</label>
          <input type="text" style={inp} placeholder="Any additional notes" value={remarks} onChange={e => setRemarks(e.target.value)}
            onFocus={e => e.target.style.borderColor = '#1B3B5F'} onBlur={e => e.target.style.borderColor = '#E8E4DF'} />
        </div>

        <div style={{ display: 'flex', gap: 15, marginTop: 30 }}>
          <button onClick={onClose}
            style={{ flex: 1, padding: 15, background: '#E8E4DF', color: '#2C2C2C', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, cursor: 'pointer' }}
            onMouseEnter={e => e.target.style.background = '#C4BEB8'} onMouseLeave={e => e.target.style.background = '#E8E4DF'}>
            Cancel
          </button>
          <button onClick={handleCollect} disabled={saving}
            style={{ flex: 1, padding: 15, background: '#2D5F4F', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}
            onMouseEnter={e => { if (!saving) { e.target.style.background = '#25503F'; e.target.style.transform = 'translateY(-2px)'; }}}
            onMouseLeave={e => { e.target.style.background = '#2D5F4F'; e.target.style.transform = 'none'; }}>
            {saving ? 'Processing…' : 'Collect Payment'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   STUDENT FEE DETAILS
─────────────────────────────────────────────── */
const StudentFeeDetails = ({ onNavigate, onLogout, studentId }) => {
  const [detail,     setDetail]     = useState(null);
  const [admission,  setAdmission]  = useState(null); // raw admission for installments
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [showModal,  setShowModal]  = useState(false);

  const admissionId = studentId;

  const loadDetail = () => {
    if (!admissionId) { setError('No admission ID provided'); setLoading(false); return; }
    setLoading(true);

    // Fetch finance detail + raw admission in parallel
    Promise.all([
      financeService.getStudentFeeDetail(admissionId),
      admissionService.getById(admissionId).catch(() => null),
    ])
      .then(([feeDetail, rawAdmission]) => {
        setDetail(feeDetail);
        setAdmission(rawAdmission);
        setError('');
      })
      .catch(() => setError('Could not load student fee details'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadDetail(); }, [admissionId]); // eslint-disable-line

  const cardStyle = { background: 'white', borderRadius: 12, padding: 30, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' };
  const cardHeaderStyle = { display: 'flex', alignItems: 'center', gap: 12, paddingBottom: 20, marginBottom: 25, borderBottom: '2px solid #E8E4DF' };

  /* ── Loading ── */
  if (loading) return (
    <div className="app-layout">
      <Sidebar activeItem="finance" onNavigate={onNavigate} onLogout={onLogout} />
      <main className="main-content" style={{ background: '#F8F6F3', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: 18, color: '#6B6B6B' }}>Loading fee details…</div>
      </main>
    </div>
  );

  /* ── Error ── */
  if (error || !detail) return (
    <div className="app-layout">
      <Sidebar activeItem="finance" onNavigate={onNavigate} onLogout={onLogout} />
      <main className="main-content" style={{ background: '#F8F6F3', padding: 40 }}>
        <div style={{ background: '#F8D7DA', border: '1px solid #F5C6CB', borderRadius: 8, padding: '20px 25px', color: '#721C24' }}>
          ⚠️ {error || 'Student not found'}
          <button onClick={() => onNavigate('finance')} style={{ marginLeft: 20, padding: '8px 16px', background: '#1B3B5F', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>← Back to Finance</button>
        </div>
      </main>
    </div>
  );

  /* ── Compute installment schedule ── */
  // Try API response first (detail.schedule), then fall back to parsing raw admission
  let schedule = detail.schedule && detail.schedule.length > 0
    ? detail.schedule
    : parseInstallments(admission, detail.totalFees);

  /* ── Fee breakdown rows ── */
  const fmtAmt = (n) => '₹' + Number(n||0).toLocaleString('en-IN');
  const roomTypeLabel = {
    '4sharing': '4 Sharing', '2sharing': '2 Sharing (Twin)',
    '2sharingPremium': '2 Sharing (Premium)', 'private': 'Single / Private',
  };
  const feeBreakdown = [
    ...(detail.roomType ? [{ label: 'Room Type',    amount: roomTypeLabel[detail.roomType] || detail.roomType, type: 'normal' }] : []),
    ...(detail.months   ? [{ label: 'Months Billed', amount: detail.months + (detail.months === 1 ? ' month' : ' months'), type: 'normal' }] : []),
    { label: 'Accommodation Charges',         amount: fmtAmt(detail.hostelFees),          type: 'normal'  },
    { label: 'Food Charges',                  amount: fmtAmt(detail.foodCharges),          type: 'normal'  },
    { label: 'Maintenance Fix Charges',       amount: fmtAmt(detail.maintenanceCharges),   type: 'normal'  },
    { label: 'Refundable Security Deposit',   amount: fmtAmt(detail.refundableDeposit),    type: 'normal'  },
    { label: 'Prepaid Card Load',             amount: fmtAmt(detail.prepaidCard),          type: 'normal'  },
    { label: 'Mattress & Pillow',             amount: fmtAmt(detail.mattressCharge),       type: 'normal'  },
    ...(detail.gst > 0 ? [{ label: 'GST',    amount: fmtAmt(detail.gst),                  type: 'normal' }] : []),
    { label: 'Total Payable',                 amount: detail.totalFmt,                     type: 'total'   },
    { label: 'Amount Paid',                   amount: detail.paidFmt,                      type: 'paid'    },
    { label: 'Balance Due',                   amount: detail.balanceFmt,                   type: 'pending' },
  ];

  /* ── PayCard data: real if available, hardcoded placeholder otherwise ── */
  const pc = detail.paycard || {
    balance: 0, totalTopup: 0, totalSpent: 0, transactionCount: 0,
    status: 'inactive', isPlaceholder: true,
  };

  return (
    <div className="app-layout">
      <Sidebar activeItem="finance" onNavigate={onNavigate} onLogout={onLogout} />
      <main className="main-content" style={{ background: '#F8F6F3' }}>

        {/* ── TOP BAR ── */}
        <div style={{ background: 'white', padding: '20px 40px', borderBottom: '1px solid #E8E4DF' }}>
          <div style={{ fontSize: 13, color: '#6B6B6B', marginBottom: 10 }}>
            <span style={{ color: '#D4AF37', cursor: 'pointer' }} onClick={() => onNavigate('finance')}>Finance</span>
            {' / '}
            <span style={{ color: '#D4AF37', cursor: 'pointer' }} onClick={() => onNavigate('finance')}>Admission Fees</span>
            {' / '}
            <span>Student Fee Details</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h1 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 32, fontWeight: 400, color: '#1B3B5F', margin: 0 }}>
              {detail.studentName}
            </h1>
            <div style={{ background: 'linear-gradient(135deg, #D4AF37 0%, #C19A2E 100%)', color: '#1B3B5F', padding: '10px 25px', borderRadius: 25, fontSize: 14, fontWeight: 700, letterSpacing: 1 }}>
              Student ID: {detail.admissionNumber}
            </div>
          </div>
        </div>

        <div style={{ padding: 40, maxWidth: 1400, margin: '0 auto' }}>

          {/* ── STUDENT INFO CARD ── */}
          <div style={{ ...cardStyle, display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 30, alignItems: 'center', marginBottom: 30 }}>
            <div style={{ width:100, height:100, borderRadius:'50%', border:'4px solid #D4AF37', overflow:'hidden', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', background:'#1B3B5F' }}>
              {detail.photo
                ? <img
                    src={studentService.getPhotoUrl(detail.studentId)}
                    alt="Student"
                    style={{ width:'100%', height:'100%', objectFit:'cover' }}
                    onError={e => { e.target.style.display='none'; e.target.parentNode.querySelector('.photo-fallback').style.display='flex'; }}
                  />
                : null}
              <div className="photo-fallback" style={{ display: detail.photo ? 'none' : 'flex', width:'100%', height:'100%', alignItems:'center', justifyContent:'center', fontSize:36, color:'white', fontWeight:700 }}>
                {detail.studentName?.[0]?.toUpperCase() || '👤'}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
              {[
                { label: 'Room Number',    val: detail.roomCode      },
                { label: 'Admission Date', val: detail.admissionDate },
                { label: 'Stream',         val: detail.stream        },
                { label: 'Mobile',         val: detail.mobile        },
                { label: 'Email',          val: detail.email         },
                { label: 'Valid Till',     val: detail.validTill     },
              ].map(f => (
                <div key={f.label} style={{ display: 'flex', flexDirection: 'column' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#6B6B6B', marginBottom: 5 }}>{f.label}</div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#2C2C2C' }}>{f.val || '—'}</div>
                </div>
              ))}
            </div>
            <div>
              {detail.balance > 0 && (
                <button onClick={() => setShowModal(true)}
                  style={{ padding: '15px 30px', background: '#2D5F4F', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, cursor: 'pointer', whiteSpace: 'nowrap' }}
                  onMouseEnter={e => { e.target.style.background = '#25503F'; e.target.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={e => { e.target.style.background = '#2D5F4F'; e.target.style.transform = 'none'; }}>
                  💰 Collect Payment
                </button>
              )}
            </div>
          </div>

          {/* ── FEE BREAKDOWN + PAYMENT SCHEDULE ── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 30, marginBottom: 30 }}>

            {/* Fee Breakdown */}
            <div style={cardStyle}>
              <div style={cardHeaderStyle}>
                <span style={{ fontSize: 24 }}>💳</span>
                <h2 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 22, fontWeight: 600, color: '#1B3B5F', margin: 0 }}>Fee Breakdown</h2>
              </div>
              {feeBreakdown.map((f, i) => {
                if (f.type === 'total') return (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#F8F6F3', padding: '20px 15px', marginTop: 15, borderRadius: 8, border: '2px solid #D4AF37' }}>
                    <span style={{ fontSize: 16, color: '#1B3B5F', fontWeight: 700 }}>{f.label}</span>
                    <span style={{ fontSize: 24, fontWeight: 700, color: '#D4AF37' }}>{f.amount}</span>
                  </div>
                );
                if (f.type === 'paid') return (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '15px 0 15px 15px', borderLeft: '4px solid #2D5F4F', marginTop: 30 }}>
                    <span style={{ fontSize: 14, color: '#6B6B6B', fontWeight: 600 }}>{f.label}</span>
                    <span style={{ fontSize: 16, fontWeight: 700, color: '#2D5F4F' }}>{f.amount}</span>
                  </div>
                );
                if (f.type === 'pending') return (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '15px 0 15px 15px', borderLeft: `4px solid ${detail.balance > 0 ? '#DC3545' : '#2D5F4F'}`, marginTop: 8 }}>
                    <span style={{ fontSize: 14, color: '#6B6B6B', fontWeight: 600 }}>{f.label}</span>
                    <span style={{ fontSize: 16, fontWeight: 700, color: detail.balance > 0 ? '#DC3545' : '#2D5F4F' }}>{f.amount}</span>
                  </div>
                );
                return (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '15px 0', borderBottom: '1px solid #E8E4DF' }}>
                    <span style={{ fontSize: 14, color: '#6B6B6B', fontWeight: 600 }}>{f.label}</span>
                    <span style={{ fontSize: 16, fontWeight: 700, color: '#2C2C2C' }}>{f.amount}</span>
                  </div>
                );
              })}
            </div>

            {/* ── PAYMENT SCHEDULE ── */}
            <div style={cardStyle}>
              <div style={cardHeaderStyle}>
                <span style={{ fontSize: 24 }}>📅</span>
                <h2 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 22, fontWeight: 600, color: '#1B3B5F', margin: 0 }}>Payment Schedule</h2>
              </div>

              {schedule.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '30px 20px', color: '#6B6B6B' }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
                  <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>No installment schedule set</div>
                  <div style={{ fontSize: 13 }}>Installment dates can be added from the student's admission record.</div>
                </div>
              ) : (
                <div style={{ position: 'relative' }}>
                  {/* vertical timeline line */}
                  <div style={{ position: 'absolute', left: 20, top: 8, bottom: 8, width: 2, background: '#E8E4DF' }} />
                  {schedule.map((p, i) => (
                    <div key={i} style={{ position: 'relative', paddingLeft: 60, paddingBottom: i < schedule.length - 1 ? 28 : 0 }}>
                      {/* dot */}
                      <div style={{
                        position: 'absolute', left: 13, top: 4,
                        width: 16, height: 16, borderRadius: '50%',
                        border: `3px solid ${p.status === 'paid' ? '#2D5F4F' : '#F59E0B'}`,
                        background: p.status === 'paid' ? '#2D5F4F' : 'white',
                        zIndex: 1,
                      }} />
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#6B6B6B', marginBottom: 4 }}>{p.date}</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: '#1B3B5F', marginBottom: 6 }}>
                        ₹{Number(p.amount).toLocaleString('en-IN')}
                      </div>
                      <span style={{
                        display: 'inline-block', padding: '4px 12px', borderRadius: 12,
                        fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                        background: p.status === 'paid' ? '#D4EDDA' : '#FFF3CD',
                        color:      p.status === 'paid' ? '#155724'  : '#856404',
                      }}>
                        {p.status === 'paid' ? '✓ Paid' : 'Pending'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── PAY CARD SUMMARY (always shown) ── */}
          <div style={{ marginBottom: 30 }}>
            <h2 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 28, color: '#1B3B5F', marginBottom: 20 }}>
              💳 Pay Card
            </h2>

            {pc.isPlaceholder ? (
              /* ─── NOT YET ACTIVATED ─── */
              <div style={{
                background: 'linear-gradient(135deg, #1B3B5F 0%, #0D1F2D 100%)',
                color: 'white', borderRadius: 16, padding: 40,
                boxShadow: '0 8px 30px rgba(27,59,95,0.3)',
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 40, alignItems: 'center' }}>
                  <div>
                    {/* card chip + header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 28 }}>
                      <div style={{ width: 44, height: 32, background: 'linear-gradient(135deg,#D4AF37,#C19A2E)', borderRadius: 6 }} />
                      <div>
                        <div style={{ fontSize: 11, opacity: 0.6, textTransform: 'uppercase', letterSpacing: 1.5 }}>Hostel Pay Card</div>
                        <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 2, opacity: 0.85 }}>
                          {detail.admissionNumber || '•••• ••••'}
                        </div>
                      </div>
                    </div>

                    <div style={{ fontSize: 13, opacity: 0.7, textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 8 }}>Current Balance</div>
                    <div style={{ fontSize: 52, fontWeight: 700, fontFamily: 'Cormorant Garamond, serif', lineHeight: 1, marginBottom: 22 }}>₹0.00</div>

                    {/* stats row */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 24 }}>
                      {[
                        { label: 'Total Top-up', val: '₹0' },
                        { label: 'Total Spent',  val: '₹0' },
                        { label: 'Transactions', val: '0'  },
                      ].map((s, i) => (
                        <div key={i} style={{ borderLeft: '3px solid rgba(255,255,255,0.2)', paddingLeft: 14 }}>
                          <div style={{ fontSize: 10, opacity: 0.6, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>{s.label}</div>
                          <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'Cormorant Garamond, serif' }}>{s.val}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* action + status */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14, alignItems: 'flex-end' }}>
                    <button
                      onClick={() => onNavigate('payCardDetails', { payCardStudentId: detail.admissionId })}
                      style={{ padding: '14px 28px', background: '#D4AF37', color: '#1B3B5F', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, cursor: 'pointer', whiteSpace: 'nowrap' }}
                      onMouseEnter={e => e.target.style.background = '#C19A2E'}
                      onMouseLeave={e => e.target.style.background = '#D4AF37'}>
                      💰 Activate &amp; Top-up
                    </button>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, opacity: 0.6, fontSize: 13 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#aaa' }} />
                      Card Not Yet Activated
                    </div>
                  </div>
                </div>

                {/* bottom banner */}
                <div style={{ marginTop: 30, paddingTop: 20, borderTop: '1px solid rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', gap: 12, opacity: 0.75 }}>
                  <span style={{ fontSize: 18 }}>ℹ️</span>
                  <span style={{ fontSize: 13 }}>
                    This student doesn't have an active pay card yet. Top up for the first time to activate it automatically.
                  </span>
                </div>
              </div>

            ) : (
              /* ─── ACTIVE CARD ─── */
              <div style={{
                background: 'linear-gradient(135deg, #1B3B5F 0%, #0D1F2D 100%)',
                color: 'white', borderRadius: 16, padding: 40,
                boxShadow: '0 8px 30px rgba(27,59,95,0.3)',
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 40, alignItems: 'center' }}>
                  <div>
                    {/* card chip + header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 28 }}>
                      <div style={{ width: 44, height: 32, background: 'linear-gradient(135deg,#D4AF37,#C19A2E)', borderRadius: 6 }} />
                      <div>
                        <div style={{ fontSize: 11, opacity: 0.6, textTransform: 'uppercase', letterSpacing: 1.5 }}>Hostel Pay Card</div>
                        <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 2, opacity: 0.85 }}>
                          {detail.admissionNumber}
                        </div>
                      </div>
                      {/* status badge */}
                      <div style={{
                        marginLeft: 'auto',
                        padding: '5px 14px', borderRadius: 20, fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                        background: pc.status === 'blocked' ? 'rgba(220,53,69,0.25)' : pc.balance < 2000 ? 'rgba(245,158,11,0.25)' : 'rgba(45,95,79,0.35)',
                        color:      pc.status === 'blocked' ? '#FF6B6B' : pc.balance < 2000 ? '#FFD93D' : '#6EE7B7',
                        border:     `1px solid ${pc.status === 'blocked' ? 'rgba(220,53,69,0.4)' : pc.balance < 2000 ? 'rgba(245,158,11,0.4)' : 'rgba(45,95,79,0.4)'}`,
                      }}>
                        {pc.status === 'blocked' ? '🔒 Blocked' : pc.balance < 2000 ? '⚠ Low Balance' : '✓ Active'}
                      </div>
                    </div>

                    <div style={{ fontSize: 13, opacity: 0.7, textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 8 }}>Current Balance</div>
                    <div style={{ fontSize: 52, fontWeight: 700, fontFamily: 'Cormorant Garamond, serif', lineHeight: 1, marginBottom: 22 }}>
                      ₹{Number(pc.balance).toLocaleString('en-IN')}
                    </div>

                    {/* stats row */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 24 }}>
                      {[
                        { label: 'Total Top-up',  val: `₹${Number(pc.totalTopup).toLocaleString('en-IN')}` },
                        { label: 'Total Spent',   val: `₹${Number(pc.totalSpent).toLocaleString('en-IN')}` },
                        { label: 'Transactions',  val: String(pc.transactionCount) },
                      ].map((s, i) => (
                        <div key={i} style={{ borderLeft: '3px solid rgba(255,255,255,0.2)', paddingLeft: 14 }}>
                          <div style={{ fontSize: 10, opacity: 0.6, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>{s.label}</div>
                          <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'Cormorant Garamond, serif' }}>{s.val}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* actions */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <button
                      onClick={() => onNavigate('payCardDetails', { payCardStudentId: detail.admissionId })}
                      style={{ padding: '14px 28px', background: '#D4AF37', color: '#1B3B5F', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, cursor: 'pointer', whiteSpace: 'nowrap' }}
                      onMouseEnter={e => { e.target.style.background = '#C19A2E'; e.target.style.transform = 'translateY(-2px)'; }}
                      onMouseLeave={e => { e.target.style.background = '#D4AF37'; e.target.style.transform = 'none'; }}>
                      View Full Details
                    </button>
                    <button
                      onClick={() => onNavigate('payCardDetails', { payCardStudentId: detail.admissionId })}
                      style={{ padding: '14px 28px', background: 'transparent', color: 'white', border: '2px solid rgba(255,255,255,0.4)', borderRadius: 8, fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, cursor: 'pointer', whiteSpace: 'nowrap' }}
                      onMouseEnter={e => { e.target.style.background = 'rgba(255,255,255,0.1)'; }}
                      onMouseLeave={e => { e.target.style.background = 'transparent'; }}>
                      💰 Top-up Card
                    </button>
                  </div>
                </div>

                {/* low balance warning */}
                {pc.balance < 2000 && pc.status !== 'blocked' && (
                  <div style={{ marginTop: 24, padding: '12px 18px', background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
                    <span>⚠️</span>
                    <span>Balance is below ₹2,000. Please top-up to avoid service interruption.</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── PAYMENT HISTORY ── */}
          <div style={cardStyle}>
            <div style={cardHeaderStyle}>
              <span style={{ fontSize: 24 }}>📋</span>
              <h2 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 22, fontWeight: 600, color: '#1B3B5F', margin: 0 }}>Payment History</h2>
            </div>

            {!detail.history || detail.history.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: '#6B6B6B' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>📄</div>
                <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>No payments recorded yet</div>
                <div style={{ fontSize: 13 }}>Payments collected using the "Collect Payment" button will appear here.</div>
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 20 }}>
                <thead>
                  <tr style={{ background: '#F8F6F3', borderBottom: '2px solid #E8E4DF' }}>
                    {['Date', 'Amount', 'Payment Mode', 'Transaction ID', 'Received By', 'Remarks'].map(h => (
                      <th key={h} style={{ padding: 12, textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#6B6B6B' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {detail.history.map((r, i) => (
                    <tr key={i} onMouseEnter={e => e.currentTarget.style.background = '#F8F6F3'} onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                      <td style={{ padding: '15px 12px', borderBottom: '1px solid #E8E4DF', fontSize: 14 }}>{r.paymentDate}</td>
                      <td style={{ padding: '15px 12px', borderBottom: '1px solid #E8E4DF', fontSize: 14, color: '#2D5F4F', fontWeight: 700 }}>₹{Number(r.amount).toLocaleString('en-IN')}</td>
                      <td style={{ padding: '15px 12px', borderBottom: '1px solid #E8E4DF', fontSize: 14 }}>{r.paymentMode}</td>
                      <td style={{ padding: '15px 12px', borderBottom: '1px solid #E8E4DF', fontSize: 14 }}>{r.transactionId || '—'}</td>
                      <td style={{ padding: '15px 12px', borderBottom: '1px solid #E8E4DF', fontSize: 14 }}>{r.receivedBy}</td>
                      <td style={{ padding: '15px 12px', borderBottom: '1px solid #E8E4DF', fontSize: 14, color: '#6B6B6B' }}>{r.remarks || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

        </div>
      </main>

      {showModal && (
        <CollectPaymentModal
          admissionId={detail.admissionId}
          studentName={detail.studentName}
          balance={detail.balance}
          onClose={() => setShowModal(false)}
          onSuccess={() => { setShowModal(false); loadDetail(); }}
        />
      )}
    </div>
  );
};

export default StudentFeeDetails;